import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { UserManagement } from '../types';
import './UsersPage.css';

const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getAllUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      setError(err.detail || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('¿Cambiar el estado de este usuario?')) return;

    try {
      setToggling(userId);
      await apiClient.toggleUserActive(userId);
      await loadUsers();
    } catch (err: any) {
      setError(err.detail || 'Error al cambiar estado del usuario');
    } finally {
      setToggling(null);
    }
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="users-loading">
        <div className="spinner-large"></div>
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <header className="users-header">
        <div className="users-header-inner">
          <h1>Gestion de Usuarios</h1>
          <button onClick={() => navigate('/admin')} className="users-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Volver al Admin
          </button>
        </div>
      </header>

      <main className="users-content">
        {error && (
          <div className="users-error-banner">
            <p>{error}</p>
            <button onClick={() => setError(null)} className="users-error-close">x</button>
          </div>
        )}

        <div className="users-summary">
          <div className="users-stat">
            <span className="users-stat-value">{users.length}</span>
            <span className="users-stat-label">Total</span>
          </div>
          <div className="users-stat">
            <span className="users-stat-value">{users.filter(u => u.is_active).length}</span>
            <span className="users-stat-label">Activos</span>
          </div>
          <div className="users-stat">
            <span className="users-stat-value">{users.filter(u => u.is_admin).length}</span>
            <span className="users-stat-label">Admins</span>
          </div>
        </div>

        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Conversaciones</th>
                <th>Registrado</th>
                <th>Ultimo acceso</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className={!u.is_active ? 'row-inactive' : ''}>
                  <td>
                    <div className="user-cell">
                      <div className="user-cell-avatar">
                        {u.email.charAt(0).toUpperCase()}
                      </div>
                      <span className="user-cell-name">{u.email}</span>
                    </div>
                  </td>
                  <td className="cell-email">{u.email}</td>
                  <td className="cell-center">{u.conversation_count}</td>
                  <td className="cell-date">{formatDate(u.created_at)}</td>
                  <td className="cell-date">{formatDate(u.last_login_at!)}</td>
                  <td>
                    <span className={`role-badge ${u.is_admin ? 'admin' : 'user'}`}>
                      {u.is_admin ? 'Admin' : 'Usuario'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                      {u.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(u.user_id)}
                      disabled={toggling === u.user_id || u.user_id === user?.user_id}
                      className={`toggle-btn ${u.is_active ? 'deactivate' : 'activate'}`}
                      title={u.user_id === user?.user_id ? 'No puedes desactivarte a ti mismo' : u.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {toggling === u.user_id ? (
                        <span className="spinner-small"></span>
                      ) : u.is_active ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      )}
                      {u.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-row">No hay usuarios registrados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default UsersPage;
